import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { createTheme } from '@mui/material/styles';
import { formatInTimeZone } from 'date-fns-tz';
import dateMath from '@elastic/datemath';

import { DateTime } from 'luxon';
import { IconReorder } from '@tabler/icons-react';

const { palette } = createTheme();

export const jobStatusColorMap = {
  success: palette.success.light,
  queued: palette.grey[400],
  running: palette.primary.light,
  failed: palette.error.light,
  pushed: palette.grey[400],
};

export const jobIconStatusMap = {
  success: <DoneIcon htmlColor={jobStatusColorMap.success} />,
  running: <PlayArrowIcon htmlColor={jobStatusColorMap.running} />,
  failed: <CloseIcon htmlColor={jobStatusColorMap.failed} />,
  pushed: <CloudUploadIcon htmlColor={jobStatusColorMap.pushed} />,
  retry: <ReplayIcon htmlColor={'info'} />,
  queued: <IconReorder />,
  dequeued: <IconReorder />,
};

const now = new Date();
export const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
export const dateFormatString = 'MMM dd, yyyy h:mm:ss a zzz';
export const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get system's local time zone
export const formatTime =
  (timezone = timeZone, dateFormat = dateFormatString) =>
  (date) => {
    try {
      return formatInTimeZone(date, timezone, dateFormat);
    } catch (error) {
      console.error(error);
      return date;
    }
  };

/**
 * Convert a cron expression (minute, hour, day, month, weekday) to a specific timezone
 * @param {string} cronExpr - The cron expression (supports standard cron format: "m h d M w")
 * @param {string} timezone - The target timezone (e.g., "America/New_York")
 * @returns {string} - Array of converted time expressions
 */
export const convertCronToTZ = (cronExpr, timezone) => {
  cronExpr = cronExpr.trim();
  const parts = cronExpr.split(' ');
  if (parts.length !== 5) {
    console.error(
      `Invalid cron expression format. Expected 'm h d M w'. Got ${cronExpr}`,
    );
  }

  let [minute, hour, day, month, weekday] = parts;

  // If both hour and minute are '*', return unchanged
  if (['*', '/'].includes(hour) || ['*', '/'].includes(minute)) {
    return `${minute} ${hour} ${day} ${month} ${weekday}`;
  }

  // Get current UTC time and convert to the target timezone
  const now = DateTime.utc().setZone(timezone);

  let convertedHour = hour;
  let convertedMinute = minute;

  // Convert hour and minute if they are numeric
  if (hour !== '*' && !isNaN(hour)) {
    const cronTime = DateTime.utc()
      .set({ hour: parseInt(hour), minute: 0 })
      .setZone(timezone, { keepLocalTime: false });
    convertedHour = cronTime.hour;
  }

  if (minute !== '*' && !isNaN(minute)) {
    const cronTime = DateTime.utc()
      .set({ hour: 0, minute: parseInt(minute) })
      .setZone(timezone, { keepLocalTime: true });
    convertedMinute = cronTime.minute;
  }

  return `${convertedMinute} ${convertedHour} ${day} ${month} ${weekday}`;
};

export const getTZAbbrv = (timezone) => {
  return formatTZ(toZonedTime(new Date(), timezone), 'zzz', {
    timeZone: timezone,
  });
};

// adapted from https://eui.elastic.co/docs/components/forms/date-and-time/super-date-picker
export const stringToMoment = (dateString, startOrEnd) => {
  let moment = null;
  if (startOrEnd === 'end') {
    moment = dateMath.parse(dateString, { roundUp: true });
  } else {
    moment = dateMath.parse(dateString);
  }
  // dateMath.parse is inconsistent with unparsable strings.
  // Sometimes undefined is returned, other times an invalid moment is returned
  if (!moment || !moment.isValid()) {
    throw new Error(
      `Unable to parse ${startOrEnd} dateString: "${dateString}"`,
    );
  }
  return moment;
};
